<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useTaskNavigation } from 'dashboard/composables/useTaskNavigation';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TaskStatusSelect from './TaskStatusSelect.vue';

const props = defineProps({
  task: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['save']);

const { t } = useI18n();
const store = useStore();
const { openTask } = useTaskNavigation();
const agentsList = useMapGetter('agents/getAgents');

const dialogRef = ref(null);

const taskDescription = ref('');
const taskDueAt = ref('');
const taskStatus = ref('todo');
const shouldAssign = ref(false);
const taskAssigneeId = ref('');

const toDatetimeLocal = unixTimestamp => {
  if (!unixTimestamp) return '';
  const date = new Date(unixTimestamp * 1000);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const resetForm = () => {
  taskDescription.value = props.task.description || '';
  taskDueAt.value = toDatetimeLocal(props.task.dueAt);
  taskStatus.value = props.task.status || 'todo';
  shouldAssign.value = !!props.task.assignee;
  taskAssigneeId.value = props.task.assignee?.id
    ? String(props.task.assignee.id)
    : '';
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

defineExpose({ open, close });

const contactName = computed(
  () => props.task.contact?.name || t('TASKS_DASHBOARD.UNNAMED_CONTACT')
);

const companyName = computed(() => props.task.contact?.company?.name || '');

const assigneeOptions = computed(() =>
  (agentsList.value || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  }))
);

const assigneeLabel = computed(
  () =>
    assigneeOptions.value.find(option => option.value === taskAssigneeId.value)
      ?.label || t('CONTACTS_LAYOUT.SIDEBAR.TASKS.ASSIGNEE_LABEL')
);

const onToggleAssign = () => {
  shouldAssign.value = !shouldAssign.value;
  if (!shouldAssign.value) {
    taskAssigneeId.value = '';
  }
};

const onSave = () => {
  if (!taskDescription.value.trim()) return;

  emit('save', {
    id: props.task.id,
    description: taskDescription.value.trim(),
    due_at: taskDueAt.value ? new Date(taskDueAt.value).toISOString() : null,
    status: taskStatus.value,
    assignee_id: shouldAssign.value ? taskAssigneeId.value || null : null,
  });
  close();
};

const onOpenSource = () => {
  openTask(props.task);
  close();
};

onMounted(() => {
  if (!agentsList.value?.length) {
    store.dispatch('agents/get');
  }
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex w-full flex-col gap-4">
      <div class="flex items-center justify-between gap-2">
        <h3 class="text-lg font-semibold text-n-slate-12 truncate">
          {{ task.summary }}
        </h3>
        <NextButton
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-external-link"
          :label="t('TASKS_DASHBOARD.EDIT.OPEN_SOURCE')"
          @click="onOpenSource"
        />
      </div>
      <div
        class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-n-slate-11 -mt-3"
      >
        <span class="font-medium text-n-slate-12">{{ contactName }}</span>
        <span v-if="companyName">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.COMPANY_LABEL') }}:
          <span class="font-medium text-n-slate-12">{{ companyName }}</span>
        </span>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DESCRIPTION_LABEL') }}
        </label>
        <textarea
          v-model="taskDescription"
          rows="4"
          class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 resize-none"
        />
      </div>

      <div class="flex items-center gap-3">
        <div class="flex flex-col gap-1 flex-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DUE_AT_LABEL') }}
          </label>
          <input
            v-model="taskDueAt"
            type="datetime-local"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </div>
        <div class="flex flex-col gap-1 flex-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TASKS_DASHBOARD.SORT_BY.OPTIONS.STATUS') }}
          </label>
          <div class="py-1.5">
            <TaskStatusSelect v-model="taskStatus" />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label
          class="flex items-center gap-1.5 text-xs text-n-slate-10 cursor-pointer w-fit"
        >
          <input
            type="checkbox"
            class="h-3 w-3"
            :checked="shouldAssign"
            @change="onToggleAssign"
          />
          {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.ASSIGN_TOGGLE_LABEL') }}
        </label>
        <div v-if="shouldAssign" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.ASSIGNEE_LABEL') }}
          </label>
          <SelectMenu
            v-model="taskAssigneeId"
            :options="assigneeOptions"
            :label="assigneeLabel"
          />
        </div>
      </div>

      <div class="flex items-center justify-end gap-3">
        <NextButton
          faded
          slate
          type="button"
          :label="t('TASKS_DASHBOARD.EDIT.CANCEL')"
          @click="close"
        />
        <NextButton
          solid
          :label="t('CONTACTS_LAYOUT.SIDEBAR.TASKS.SAVE')"
          :disabled="!taskDescription.trim()"
          @click="onSave"
        />
      </div>
    </div>
  </Dialog>
</template>
